/**
 * 직위 템플릿 — 표준 직위 사다리를 미리 담아 두고 «골라 담게» 한다.
 *
 * ★★ **왜 만드나.** 직급표는 0행에서 시작하는데, 담당자에게 「직위 이름을 적으세요」 는
 *    빈 종이를 내미는 것과 같다. 회사마다 쓰는 이름은 거의 정해져 있으므로 **표준 목록을
 *    옆에 두고 체크해서 담게** 한다 — 다우오피스 «직위체계» 가 하는 방식이고, 실제로
 *    담당자가 하는 일은 「만들기」 가 아니라 「고르기」 다.
 *
 * ★ **레벨은 제안값이다.** 담은 뒤 표에서 고칠 수 있다. 우리 규약은 «클수록 높다» 라
 *    회장이 20, 사원이 5 다 — 다우오피스의 코드(P010010 이 회장)와 방향이 반대이므로
 *    코드를 그대로 옮기지 않고 뜻만 옮겼다.
 *
 * ★ 사이를 띄운 곳이 있다(이사 13 → 부장 11). 임원과 직원 사이처럼 **회사마다 한 칸 더
 *   끼우는 자리**를 비워 둔 것이다 — 붙여 놓으면 나중에 넣을 자리가 없다.
 */

export interface GradeTemplateItem {
  name: string;
  /** 제안 레벨 — 클수록 높다. 담은 뒤 표에서 고칠 수 있다. */
  level: number;
}

export interface GradeTemplate {
  key: string;
  label: string;
  /** 이 묶음이 누구의 것인지 한 줄. */
  note: string;
  items: GradeTemplateItem[];
}

/** ★ 기본 묶음 — 배열 첨자(`[0]`)는 타입이 `| undefined` 라 기본값으로 쓸 수 없다. */
const OFFICE: GradeTemplate = {
  key: 'office',
  label: '사무직',
  note: '임원부터 사원까지 — 가장 널리 쓰는 이름',
  items: [
    { name: '회장', level: 20 },
    { name: '부회장', level: 19 },
    { name: '대표이사', level: 18 },
    { name: '사장', level: 17 },
    { name: '부사장', level: 16 },
    { name: '전무', level: 15 },
    { name: '상무', level: 14 },
    { name: '이사', level: 13 },
    { name: '부장', level: 11 },
    { name: '차장', level: 10 },
    { name: '과장', level: 9 },
    { name: '대리', level: 8 },
    { name: '주임', level: 7 },
    { name: '사원', level: 5 },
  ],
};

export const GRADE_TEMPLATES: GradeTemplate[] = [
  OFFICE,
  {
    key: 'simple',
    label: '간소 직급',
    note: '단계를 적게 두는 회사 — 대표 · 임원 · 수석 · 책임 · 선임',
    items: [
      { name: '대표', level: 20 },
      { name: '임원', level: 16 },
      { name: '수석', level: 12 },
      { name: '책임', level: 10 },
      { name: '선임', level: 8 },
      { name: '사원', level: 5 },
    ],
  },
  {
    key: 'research',
    label: '연구 · 기술직',
    note: '연구소 · 부설연구소의 연구원 사다리',
    items: [
      { name: '연구소장', level: 16 },
      { name: '수석연구원', level: 13 },
      { name: '책임연구원', level: 11 },
      { name: '선임연구원', level: 9 },
      { name: '전임연구원', level: 7 },
      { name: '연구원', level: 5 },
      { name: '연구보조원', level: 3 },
    ],
  },
  {
    key: 'field',
    label: '생산 · 현장직',
    note: '공장 · 현장 조직의 이름',
    items: [
      { name: '공장장', level: 15 },
      { name: '부공장장', level: 13 },
      { name: '반장', level: 10 },
      { name: '조장', level: 8 },
      { name: '기능장', level: 6 },
      { name: '사원', level: 4 },
    ],
  },
];

export const DEFAULT_TEMPLATE = OFFICE;

export function templateOf(key: string): GradeTemplate {
  return GRADE_TEMPLATES.find((t) => t.key === key) ?? DEFAULT_TEMPLATE;
}

/**
 * 이미 목록에 있는 이름을 가려낸다.
 *
 * ★ **접어 둔 직위도 «이미 있다» 로 센다.** 같은 이름을 다시 넣으면 서버가 막는데(유니크),
 *   화면이 「담을 수 있다」 고 해 놓고 담을 때 실패하면 담당자가 이유를 알 수 없다.
 *   접어 둔 것은 다시 담는 게 아니라 표에서 «다시 쓰기» 를 누르는 것이다.
 */
export function markTaken(
  items: GradeTemplateItem[],
  existingNames: Iterable<string>,
): Array<GradeTemplateItem & { taken: boolean }> {
  const have = new Set<string>();
  for (const n of existingNames) have.add(n.trim());
  return items.map((it) => ({ ...it, taken: have.has(it.name) }));
}

/** 체크한 것 중 **아직 없는 것만** 담는다 — 이미 있는 것을 골라 두어도 조용히 건너뛴다. */
export function pickToAdd(
  items: GradeTemplateItem[],
  existingNames: Iterable<string>,
  checked: Iterable<string>,
): GradeTemplateItem[] {
  const want = new Set<string>();
  for (const n of checked) want.add(n);
  return markTaken(items, existingNames).filter((it) => want.has(it.name) && !it.taken);
}

/* ────────────────────────────────────────────────────────────────────
 * 화면이 쓰는 목록 만들기 (m139)
 * ──────────────────────────────────────────────────────────────────── */

/** 화면에 그릴 템플릿 한 줄. `itemId` 가 있으면 **회사가 보탠 것**이라 지울 수 있다. */
export interface TemplateRow extends GradeTemplateItem {
  callName: string | null;
  itemId: string | null;
}

/**
 * 코드의 기본 묶음 **위에** 회사가 보탠 항목을 얹는다.
 *
 * ★ 이름이 겹치면 **회사 것이 이긴다** — 회사가 일부러 레벨이나 호칭을 고쳐 보탠 것이므로
 *   기본값이 그것을 덮으면 보탠 뜻이 사라진다.
 */
export function mergeTemplateItems(
  base: readonly GradeTemplateItem[],
  extra: ReadonlyArray<{ id: string; name: string; callName: string | null; level: number }>,
): TemplateRow[] {
  const rows = new Map<string, TemplateRow>();
  for (const b of base) rows.set(b.name, { ...b, callName: null, itemId: null });
  for (const e of extra) {
    rows.set(e.name, { name: e.name, level: e.level, callName: e.callName, itemId: e.id });
  }
  return [...rows.values()].sort((a, b) => b.level - a.level || a.name.localeCompare(b.name, 'ko'));
}

/** 지금 회사에서 실제로 쓰이는 직책 한 줄. */
export interface CompanyPosition {
  name: string;
  /** 이 직책을 쓰는 재직자 수. */
  count: number;
  /** 직급표에 이미 들어 있는가. */
  inMaster: boolean;
}

/**
 * **현재 회사의 직책** — 직원들이 실제로 쓰고 있는 이름 전부.
 *
 * ★ 직급표에 있는 것과 없는 것을 **한 목록으로** 본다. 예전에는 없는 것만 «목록에 없는 직위»
 *   로 보여 줬는데, 그러면 「지금 우리 회사가 무엇을 쓰나」 를 한눈에 볼 데가 없었다.
 * ★ 쓰는 사람이 0명인 직급표 행은 여기 안 나온다 — 그건 «쓰이는 직책» 이 아니라 목록일 뿐이다.
 */
export function companyPositions(
  grades: ReadonlyArray<{ name: string; people: number }>,
  unlisted: ReadonlyArray<{ name: string; count: number }>,
): CompanyPosition[] {
  const rows: CompanyPosition[] = [];
  for (const g of grades) if (g.people > 0) rows.push({ name: g.name, count: g.people, inMaster: true });
  for (const u of unlisted) rows.push({ name: u.name, count: u.count, inMaster: false });
  return rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'ko'));
}

/**
 * 끌어다 놓은 결과의 새 차례.
 *
 * ★ 순수 함수로 떼어 둔 것은 **끌어 놓기를 눈으로만 확인할 수 없기 때문**이다.
 *   위로 옮길 때와 아래로 옮길 때 자리 계산이 한 칸씩 다르다.
 */
export function moveInOrder<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}
